/**
 * The phone's filter trigger and sheet, shared by the Knowledge Archive and
 * Quick Bytes headers.
 *
 * - `FiltersButton` - labelled "Filters", with how many are on. It used to be
 *   an unlabelled icon up by Back on both pages, far from what it narrows and
 *   silent about a feed that was filtered.
 * - `PhoneFilterSheet` - the panel (`layout='sheet'`) in a tall sheet, with
 *   `FilterSheetFooter`'s "Clear all" beside the way back.
 */

import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Sheet, cn } from '@ai-tutor/ui';
import { FilterSheetFooter } from '@/components/features/mindspace/filters/FilterSheetFooter';
import {
  countActiveFilters,
  useTagFilters,
} from '@/hooks/mindspace/useTagFilters';

export function FiltersButton({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  const active = countActiveFilters(useTagFilters(), { search: false });
  return (
    <button
      type='button'
      onClick={onClick}
      aria-haspopup='dialog'
      aria-expanded={open}
      aria-label={active ? `Filters, ${active} on` : 'Filters'}
      className={cn(
        'flex h-11 shrink-0 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'dark:tint-brand border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/60'
          : 'border-border bg-background text-foreground hover:bg-muted'
      )}
    >
      <SlidersHorizontal className='h-4 w-4' aria-hidden='true' />
      Filters
      {active > 0 && (
        <span
          aria-hidden='true'
          className='flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 font-mono text-xs font-semibold tabular-nums text-primary-foreground'
        >
          {active}
        </span>
      )}
    </button>
  );
}

export function PhoneFilterSheet({
  isOpen,
  onClose,
  doneLabel,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** "Show 24 concepts", "Show 12 bytes". */
  doneLabel: string;
  /** The page's filter panel, in its `layout='sheet'`. */
  children: ReactNode;
}) {
  const filters = useTagFilters();
  return (
    <Sheet isOpen={isOpen} onClose={onClose} title='Filters' size='tall'>
      <div className='flex h-full flex-col'>
        <div className='min-h-0 flex-1 overflow-y-auto px-4'>{children}</div>
        <FilterSheetFooter
          active={countActiveFilters(filters, { search: false })}
          onClear={filters.clearFilters}
          onDone={onClose}
          doneLabel={doneLabel}
        />
      </div>
    </Sheet>
  );
}
